import enTrade from "#/integrations/tolgee/messages/en/trade.json";
import koTrade from "#/integrations/tolgee/messages/ko/trade.json";
import zhTrade from "#/integrations/tolgee/messages/zh/trade.json";
import { getTradeMarketName, XYZ_DEX_NAME } from "./hyperliquid.ts";

type TranslationParams = Record<
	string,
	string | number | boolean | null | undefined
>;

export type MarketTranslate = (
	key: string,
	params?: TranslationParams,
) => string;

const PERPETUAL_SUFFIX = "Perpetual Futures";
const MARKET_ASSET_MESSAGES = [enTrade, koTrade, zhTrade] as const;

function translateWithFallback(
	t: MarketTranslate,
	key: string,
	fallback: string,
	params?: TranslationParams,
) {
	const translated = t(key, params);
	return translated && translated !== key ? translated : fallback;
}

export function stripPerpetualSuffix(value: string) {
	return value.replace(/ Perpetual Futures$/, "");
}

export function getDefaultPerpetualDisplayName(coin: string, dex?: string) {
	return `${getTradeMarketName(coin, dex)} ${PERPETUAL_SUFFIX}`;
}

export function getLocalizedMarketName({
	coin,
	dex,
	t,
}: {
	coin: string;
	dex?: string;
	t: MarketTranslate;
}) {
	const key = getMarketAssetTranslationKey(coin, dex);
	return translateWithFallback(t, key, getTradeMarketName(coin, dex));
}

// 이미지 SEO + a11y: 마켓 로고 <img>의 alt 단일 소스 — "{로컬라이즈드 풀네임} ({티커}) logo".
// 이름 매핑이 없어 이름이 티커로 폴백되면 "{티커} logo"로 중복 제거.
// "logo" 접미사는 영어 고정(이름부만 로케일 번역) — 필요 시 Tolgee 키로 승격.
export function getMarketLogoAlt({
	coin,
	dex,
	t,
}: {
	coin: string;
	dex?: string;
	t: MarketTranslate;
}) {
	const name = getLocalizedMarketName({ coin, dex, t });
	return name.toLowerCase() === coin.toLowerCase()
		? `${coin} logo`
		: `${name} (${coin}) logo`;
}

function getMarketAssetTranslationKey(coin: string, dex?: string) {
	return dex === XYZ_DEX_NAME
		? `market.asset.xyz.${coin.toLowerCase()}`
		: `market.asset.${coin.toLowerCase()}`;
}

export function getSearchableMarketNames({
	coin,
	dex,
}: {
	coin: string;
	dex?: string;
}) {
	const key = getMarketAssetTranslationKey(coin, dex);
	const fallback = getTradeMarketName(coin, dex);

	return Array.from(
		new Set([
			fallback,
			...MARKET_ASSET_MESSAGES.map(
				(messages) => (messages as Record<string, string>)[key],
			).filter(
				(value): value is string =>
					typeof value === "string" && value.length > 0,
			),
		]),
	);
}

export function getLocalizedPerpetualDisplayName({
	coin,
	dex,
	t,
}: {
	coin: string;
	dex?: string;
	t: MarketTranslate;
}) {
	const name = getLocalizedMarketName({ coin, dex, t });
	const fallback = getDefaultPerpetualDisplayName(coin, dex);

	return translateWithFallback(t, "market.format.perpetual-futures", fallback, {
		name,
	});
}
